/**
 * PDFPreview Component
 * Desktop PDF rendering using @react-pdf/renderer PDFViewer
 * For mobile rendering, see MobilePDFPreview.tsx
 */

import { Document, Page, Text, View, Font, StyleSheet, pdf, PDFViewer, Image } from '@react-pdf/renderer';
import type { Invoice } from '@shared/schemas/invoice.schema';
import type { PaperSize } from '@/types/paper';
import type { PageLayout } from '@/utils/pagination';
import { generateInvoiceFilename } from '@/utils/filename';

// Register fonts - using TTF format (required by @react-pdf/renderer)
// Source: https://github.com/aioneframework/cdn (via jsDelivr CDN)
Font.register({
  family: 'Roboto',
  fonts: [
    { src: 'https://cdn.jsdelivr.net/gh/aioneframework/cdn@master/assets/fonts/roboto/Roboto-Regular.ttf', fontWeight: 400 },
    { src: 'https://cdn.jsdelivr.net/gh/aioneframework/cdn@master/assets/fonts/roboto/Roboto-Medium.ttf', fontWeight: 500 },
    { src: 'https://cdn.jsdelivr.net/gh/aioneframework/cdn@master/assets/fonts/roboto/Roboto-Bold.ttf', fontWeight: 700 },
  ],
});

interface PDFPreviewProps {
  invoice: Invoice;
  paperSize: PaperSize;
  pageLayouts: PageLayout[];
}

// Format currency
function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

// Format date safely
function formatDate(date: string | Date): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString();
  } catch {
    return String(date);
  }
}

// Calculate line item total
function getLineItemTotal(quantity: number, unitPrice: number): number {
  return quantity * unitPrice;
}

// Calculate invoice totals
function calculateInvoiceTotals(invoice: Invoice) {
  const discountAmount = (invoice.subtotal * invoice.discountRate) / 100;
  const shipping = invoice.shipping || 0;
  const taxAmount = ((invoice.subtotal - discountAmount) * invoice.taxRate) / 100;
  const total = invoice.subtotal - discountAmount + shipping + taxAmount;

  return { discountAmount, shipping, taxAmount, total };
}

// Styles for the PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    padding: 20,
    fontFamily: 'Roboto',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerLeft: {
    width: '60%',
  },
  headerRight: {
    width: '40%',
    textAlign: 'right',
  },
  logo: {
    marginBottom: 8,
    height: 60,
    objectFit: 'contain',
    alignSelf: 'flex-start',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 10,
    color: '#6B7280',
  },
  invoiceNumber: {
    fontSize: 9,
    color: '#6B7280',
    marginBottom: 2,
  },
  invoiceNumberValue: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
  },
  pageNumber: {
    fontSize: 9,
    color: '#6B7280',
    marginTop: 2,
  },
  fromToSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  fromToColumn: {
    width: '48%',
  },
  sectionLabel: {
    fontSize: 9,
    color: '#6B7280',
    fontWeight: 700,
    marginBottom: 4,
  },
  companyName: {
    fontSize: 11,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 2,
  },
  contactInfo: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 2,
  },
  datesSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  dateColumn: {
    width: '48%',
  },
  dateLabel: {
    fontSize: 9,
    color: '#6B7280',
    marginBottom: 2,
  },
  dateValue: {
    fontSize: 11,
    fontWeight: 500,
    color: '#111827',
  },
  table: {
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: 'row',
    fontSize: 10,
    fontWeight: 700,
    color: '#374151',
    backgroundColor: '#F9FAFB',
    padding: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableRow: {
    flexDirection: 'row',
    fontSize: 10,
    color: '#111827',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableCellDescription: {
    width: '45%',
  },
  tableCellQty: {
    width: '15%',
    textAlign: 'center',
  },
  tableCellUnitPrice: {
    width: '20%',
    textAlign: 'right',
  },
  tableCellTotal: {
    width: '20%',
    textAlign: 'right',
  },
  totalsSection: {
    marginLeft: 'auto',
    width: 150,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    fontSize: 10,
    color: '#6B7280',
  },
  totalRowBold: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 700,
    fontSize: 12,
    color: '#111827',
    borderTopWidth: 2,
    borderTopColor: '#E5E7EB',
    paddingTop: 12,
    marginTop: 8,
  },
  notesSection: {
    marginTop: 12,
  },
  notesLabel: {
    fontSize: 9,
    color: '#6B7280',
    fontWeight: 700,
    marginBottom: 4,
  },
  notesText: {
    fontSize: 10,
    color: '#374151',
  },
  signStampSection: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginTop: 12,
  },
  signStampImage: {
    width: 80,
    height: 80,
    objectFit: 'contain',
  },
  signStampInfo: {
    marginTop: 4,
    textAlign: 'right',
  },
  signStampName: {
    fontSize: 10,
    fontWeight: 700,
    color: '#111827',
  },
  signStampRole: {
    fontSize: 9,
    color: '#6B7280',
  },
  footer: {
    marginTop: 12,
    textAlign: 'center',
  },
  footerText: {
    fontSize: 9,
    color: '#6B7280',
    marginBottom: 2,
  },
});

// PDF Document Component
const InvoiceDocument = ({ invoice, paperSize, pageLayouts }: PDFPreviewProps) => {
  const isPortrait = paperSize.orientation === 'portrait';
  const widthMM = isPortrait ? paperSize.width : paperSize.height;
  const heightMM = isPortrait ? paperSize.height : paperSize.width;

  // Convert mm to points (1mm = 2.83465 points)
  const width = widthMM * 2.83465;
  const height = heightMM * 2.83465;

  // Calculate totals
  const { discountAmount, taxAmount, total } = calculateInvoiceTotals(invoice);

  return (
    <Document>
      {pageLayouts.map((layout) => {
        const pageItems = invoice.lineItems.slice(layout.startItemIndex, layout.endItemIndex);

        return (
          <Page
            key={layout.pageNumber}
            size={{ width, height }}
            style={styles.page}
          >
            {/* Header */}
            {layout.showHeader && (
              <View style={styles.header}>
                <View style={styles.headerLeft}>
                  {invoice.fromLogo && (
                    <Image src={invoice.fromLogo} style={styles.logo} />
                  )}
                  <Text style={styles.title}>
                    {(invoice.documentType || 'invoice').toUpperCase()}
                  </Text>
                </View>
                <View style={styles.headerRight}>
                  {invoice.invoiceNumber && (
                    <>
                      <Text style={styles.invoiceNumber}>
                        {invoice.documentType === 'receipt' ? 'Receipt Number' : 'Invoice Number'}
                      </Text>
                      <Text style={styles.invoiceNumberValue}>{invoice.invoiceNumber}</Text>
                    </>
                  )}
                  {pageLayouts.length > 1 && (
                    <Text style={styles.pageNumber}>Page {layout.pageNumber} of {pageLayouts.length}</Text>
                  )}
                </View>
              </View>
            )}

            {/* From/To */}
            {layout.showFromTo && (
              <View style={styles.fromToSection}>
                <View style={styles.fromToColumn}>
                  <Text style={styles.sectionLabel}>FROM</Text>
                  <Text style={styles.companyName}>{invoice.fromCompany}</Text>
                  {invoice.fromEmail && <Text style={styles.contactInfo}>{invoice.fromEmail}</Text>}
                  {invoice.fromPhone && <Text style={styles.contactInfo}>{invoice.fromPhone}</Text>}
                  {invoice.fromAddress && <Text style={styles.contactInfo}>{invoice.fromAddress}</Text>}
                </View>
                <View style={styles.fromToColumn}>
                  <Text style={styles.sectionLabel}>BILL TO</Text>
                  <Text style={styles.companyName}>{invoice.toCompany}</Text>
                  {invoice.toEmail && <Text style={styles.contactInfo}>{invoice.toEmail}</Text>}
                  {invoice.toPhone && <Text style={styles.contactInfo}>{invoice.toPhone}</Text>}
                  {invoice.toAddress && <Text style={styles.contactInfo}>{invoice.toAddress}</Text>}
                </View>
              </View>
            )}

            {/* Dates */}
            {layout.showDates && (
              <View style={styles.datesSection}>
                <View style={styles.dateColumn}>
                  <Text style={styles.dateLabel}>Invoice Date</Text>
                  <Text style={styles.dateValue}>
                    {formatDate(invoice.invoiceDate)}
                  </Text>
                </View>
                {invoice.dueDate && (
                  <View style={styles.dateColumn}>
                    <Text style={styles.dateLabel}>Due Date</Text>
                    <Text style={styles.dateValue}>
                      {formatDate(invoice.dueDate)}
                    </Text>
                  </View>
                )}
              </View>
            )}

            {/* Line Items Table */}
            {pageItems.length > 0 && (
              <View style={styles.table}>
                <View style={styles.tableHeader}>
                  <View style={styles.tableCellDescription}>
                    <Text>DESCRIPTION</Text>
                  </View>
                  <View style={styles.tableCellQty}>
                    <Text>QTY</Text>
                  </View>
                  <View style={styles.tableCellUnitPrice}>
                    <Text>UNIT PRICE</Text>
                  </View>
                  <View style={styles.tableCellTotal}>
                    <Text>TOTAL</Text>
                  </View>
                </View>
                {pageItems.map((item, index) => (
                  <View key={item.id || index} style={styles.tableRow}>
                    <View style={styles.tableCellDescription}>
                      <Text>{item.description}</Text>
                    </View>
                    <View style={styles.tableCellQty}>
                      <Text>{item.quantity.toString()}</Text>
                    </View>
                    <View style={styles.tableCellUnitPrice}>
                      <Text>{formatCurrency(item.unitPrice, invoice.currency)}</Text>
                    </View>
                    <View style={styles.tableCellTotal}>
                      <Text style={{ fontWeight: 700 }}>
                        {formatCurrency(getLineItemTotal(item.quantity, item.unitPrice), invoice.currency)}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            )}

            {/* Totals */}
            {layout.showTotals && (
              <View style={styles.totalsSection}>
                <View style={styles.totalRow}>
                  <Text>Subtotal</Text>
                  <Text>{formatCurrency(invoice.subtotal, invoice.currency)}</Text>
                </View>
                {invoice.discountRate > 0 && (
                  <View style={styles.totalRow}>
                    <Text>Discount ({invoice.discountRate}%)</Text>
                    <Text style={{ color: '#DC2626' }}>
                      -{formatCurrency(discountAmount, invoice.currency)}
                    </Text>
                  </View>
                )}
                {invoice.shipping > 0 && (
                  <View style={styles.totalRow}>
                    <Text>Shipping</Text>
                    <Text>{formatCurrency(invoice.shipping, invoice.currency)}</Text>
                  </View>
                )}
                {invoice.taxRate > 0 && (
                  <View style={styles.totalRow}>
                    <Text>Tax ({invoice.taxRate}%)</Text>
                    <Text>{formatCurrency(taxAmount, invoice.currency)}</Text>
                  </View>
                )}
                <View style={styles.totalRowBold}>
                  <Text>TOTAL</Text>
                  <Text>{formatCurrency(total, invoice.currency)}</Text>
                </View>
              </View>
            )}

            {/* Sign/Stamp - below totals section */}
            {(invoice.fromSignStamp || invoice.fromSignStampName || invoice.fromSignStampRole) && (
              <View style={styles.signStampSection}>
                {invoice.fromSignStamp ? (
                  <Image src={invoice.fromSignStamp} style={styles.signStampImage} />
                ) : (
                  <View style={styles.signStampImage} />
                )}
                {(invoice.fromSignStampName || invoice.fromSignStampRole) && (
                  <View style={styles.signStampInfo}>
                    {invoice.fromSignStampName && (
                      <Text style={styles.signStampName}>{invoice.fromSignStampName}</Text>
                    )}
                    {invoice.fromSignStampRole && (
                      <Text style={styles.signStampRole}>{invoice.fromSignStampRole}</Text>
                    )}
                  </View>
                )}
              </View>
            )}

            {/* Notes */}
            {layout.showNotes && invoice.notes && (
              <View style={styles.notesSection}>
                <Text style={styles.notesLabel}>NOTES</Text>
                <Text style={styles.notesText}>{invoice.notes}</Text>
              </View>
            )}

            {/* Footer */}
            {layout.showFooter && invoice.dueDate && (
              <View style={styles.footer}>
                <Text style={styles.footerText}>
                  Payment due by {formatDate(invoice.dueDate)}
                </Text>
              </View>
            )}
          </Page>
        );
      })}
    </Document>
  );
};

// Desktop PDF Preview Component
export function PDFPreview({ invoice, paperSize, pageLayouts }: PDFPreviewProps) {
  return (
    <div style={{ height: '800px', width: '100%', overflow: 'hidden' }}>
      <PDFViewer
        style={{
          width: '100%',
          height: '800px',
          border: 'none',
        }}
        showToolbar={true}
      >
        <InvoiceDocument invoice={invoice} paperSize={paperSize} pageLayouts={pageLayouts} />
      </PDFViewer>
    </div>
  );
}

// Export InvoiceDocument for use in mobile preview
export { InvoiceDocument };

// Export function to download PDF
export async function exportPDFWithReactPDF(invoice: Invoice, paperSize: PaperSize, pageLayouts: PageLayout[], filename?: string) {
  const doc = <InvoiceDocument invoice={invoice} paperSize={paperSize} pageLayouts={pageLayouts} />;
  const blob = await pdf(doc).toBlob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename || generateInvoiceFilename(invoice)}.pdf`;
  link.click();

  URL.revokeObjectURL(url);
}